import { useCallback, useRef, useState, type RefObject, type TouchEvent } from 'react'
import { triggerRefresh } from '@/lib/pullToRefresh/triggerRefresh'
import {
  PULL_THRESHOLD,
  PULL_MAX_DISTANCE,
  PULL_RESISTANCE,
  SNAP_BACK_DURATION,
  SNAP_BACK_EASING,
} from '@/lib/pullToRefresh/pullToRefreshConstants'

export type PullToRefreshState = 'idle' | 'pulling' | 'triggered' | 'refreshing'

type Options = {
  enabled?: boolean
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export function usePullToRefresh({ enabled = true, scrollContainerRef }: Options = {}) {
  const [state, setState] = useState<PullToRefreshState>('idle')

  const startYRef = useRef(0)
  const startXRef = useRef(0)
  const isTrackingRef = useRef(false)
  const directionLockedRef = useRef(false)
  const pullDistanceRef = useRef(0)
  const stateRef = useRef<PullToRefreshState>('idle')
  const isAnimatingRef = useRef(false)

  // DOM refs for direct manipulation (bypasses React re-renders for 60fps)
  const contentRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const iconWrapperRef = useRef<HTMLDivElement>(null)

  const updateTransform = useCallback((distance: number) => {
    const value = `translateY(${distance}px)`
    if (contentRef.current) contentRef.current.style.transform = value
    if (indicatorRef.current) indicatorRef.current.style.transform = value
  }, [])

  const animateTo = useCallback(
    (targetDistance: number): Promise<void> => {
      return new Promise(resolve => {
        const content = contentRef.current
        const indicator = indicatorRef.current

        if (!content && !indicator) {
          pullDistanceRef.current = targetDistance
          resolve()
          return
        }

        isAnimatingRef.current = true

        // Force reflow to commit current transform position
        void content?.offsetHeight

        // Enable CSS transition and set target — browser animates from current to target
        const transition = `transform ${SNAP_BACK_DURATION}ms ${SNAP_BACK_EASING}`
        if (content) content.style.transition = transition
        if (indicator) indicator.style.transition = transition

        requestAnimationFrame(() => {
          if (content) content.style.transform = `translateY(${targetDistance}px)`
          if (indicator) indicator.style.transform = `translateY(${targetDistance}px)`
          pullDistanceRef.current = targetDistance

          setTimeout(() => {
            if (content) content.style.transition = 'none'
            if (indicator) indicator.style.transition = 'none'
            isAnimatingRef.current = false
            resolve()
          }, SNAP_BACK_DURATION)
        })
      })
    },
    [],
  )

  const cleanUpGpuLayers = useCallback(() => {
    if (contentRef.current) contentRef.current.style.willChange = ''
    if (indicatorRef.current) indicatorRef.current.style.willChange = ''
  }, [])

  const onTouchStart = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      if (!enabled) return
      if (stateRef.current === 'refreshing') return
      if (isAnimatingRef.current) return
      if (e.touches.length > 1) return

      startYRef.current = e.touches[0].clientY
      startXRef.current = e.touches[0].clientX
      isTrackingRef.current = false
      directionLockedRef.current = false
    },
    [enabled],
  )

  const onTouchMove = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      if (!enabled) return
      if (stateRef.current === 'refreshing') return
      if (isAnimatingRef.current) return
      if (e.touches.length > 1) return

      const currentY = e.touches[0].clientY
      const currentX = e.touches[0].clientX
      const deltaY = currentY - startYRef.current
      const deltaX = currentX - startXRef.current

      if (!directionLockedRef.current) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          isTrackingRef.current = false
          directionLockedRef.current = true
          return
        }

        const scrollY = scrollContainerRef?.current?.scrollTop ?? window.scrollY
        if (deltaY > 0 && scrollY <= 0) {
          isTrackingRef.current = true
          directionLockedRef.current = true
          // Promote to GPU layer for smooth transforms
          if (contentRef.current) contentRef.current.style.willChange = 'transform'
          if (indicatorRef.current) indicatorRef.current.style.willChange = 'transform'
        } else {
          directionLockedRef.current = true
          return
        }
      }

      if (!isTrackingRef.current) return

      const rawDistance = deltaY * PULL_RESISTANCE
      const clampedDistance = Math.min(Math.max(rawDistance, 0), PULL_MAX_DISTANCE)

      pullDistanceRef.current = clampedDistance

      // Direct DOM updates — no React re-renders
      updateTransform(clampedDistance)

      // Rotate icon based on pull progress
      if (iconWrapperRef.current) {
        const rotation = Math.min((clampedDistance / PULL_THRESHOLD) * 180, 180)
        iconWrapperRef.current.style.transform = `rotate(${rotation}deg)`
      }

      // Only trigger React re-render on state transition (threshold crossing)
      const isTriggered = clampedDistance >= PULL_THRESHOLD
      const newState = isTriggered ? 'triggered' : 'pulling'
      if (stateRef.current !== newState) {
        stateRef.current = newState
        setState(newState)
      }
    },
    [enabled, scrollContainerRef, updateTransform],
  )

  const onTouchEnd = useCallback(() => {
    if (!enabled) return
    if (stateRef.current === 'refreshing') return
    if (isAnimatingRef.current) return

    const wasTriggered = stateRef.current === 'triggered'

    isTrackingRef.current = false
    directionLockedRef.current = false

    if (wasTriggered) {
      stateRef.current = 'refreshing'
      setState('refreshing')

      // Snap to threshold position (content is close, so instant is fine)
      updateTransform(PULL_THRESHOLD)
      pullDistanceRef.current = PULL_THRESHOLD

      triggerRefresh()
        .then(() => animateTo(0))
        .then(() => {
          cleanUpGpuLayers()
          stateRef.current = 'idle'
          setState('idle')
        })
        .catch(() => {
          animateTo(0).then(() => {
            cleanUpGpuLayers()
            stateRef.current = 'idle'
            setState('idle')
          })
        })
    } else if (stateRef.current === 'pulling') {
      // Animate back to rest position, then transition to idle
      animateTo(0).then(() => {
        cleanUpGpuLayers()
        stateRef.current = 'idle'
        setState('idle')
      })
    }
  }, [enabled, updateTransform, animateTo, cleanUpGpuLayers])

  return {
    state,
    contentRef,
    indicatorRef,
    iconWrapperRef,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  }
}
